import { TextField } from '@qbe/ads-core';
import React from 'react';
import { Controller } from 'react-hook-form';

import FormSpacer from '~/components-ds/FormSpacer';
import Modal from '~/components-ds/Modal';
import { useAppContent } from '~/hooks';
import { useFormWithAnalytics } from '~/hooks/useFormWithAnalytics';
import { PlumbingLicence } from '~/types/cover';

interface FormData {
  name: string;
  number: string;
}

interface UpsertPlumberProps {
  index?: number;
  onAdd?: (entity: PlumbingLicence) => void;
  onUpdate?: (entity: PlumbingLicence, id: number) => void;
  onClose?: () => void;
  mode: 'insert' | 'update';
  entity?: PlumbingLicence;
  nameFieldLength?: number;
}

export const UpsertPlumber: React.FC<UpsertPlumberProps> = ({
  index = 0,
  onClose,
  mode = 'insert',
  onAdd,
  onUpdate,
  entity,
  nameFieldLength = 30,
}) => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent } = useAppContent({
    basePath: 'dsme.coverDetailsBusinessLiabilityPlumbing.fields',
  });

  const { handleSubmit, formState, control } = useFormWithAnalytics<FormData>({
    mode: 'onChange',
    defaultValues: entity,
  });

  const doSubmit = (data: PlumbingLicence | undefined) => {
    if (!data) return;
    if (mode === 'insert') onAdd?.(data);
    else onUpdate?.(data, index);
    onClose?.();
  };

  return (
    <Modal
      open
      analyticsModalName="upsert-plumber-modal"
      id={`${mode}-modal`}
      title={
        mode === 'insert' ? getTextContent('addModal.title') : getTextContent('updateModal.title')
      }
      primaryBtnProps={{
        label:
          mode === 'insert'
            ? getSharedContent('addButtonLabel')
            : getSharedContent('updateButtonLabel'),
        onClick: handleSubmit(doSubmit),
        'data-track': `${mode === 'insert' ? 'Add' : 'Update'} - plumbing licence`,
        disabled: !formState.isValid,
      }}
      secondaryBtnProps={{
        label: getSharedContent('closeModalButtonLabel'),
        onClick: onClose,
        'data-track': 'Cancel - upsert plumber',
      }}
    >
      <form>
        <Controller
          control={control}
          render={({ field: { ref: _, ...field }, fieldState }) => (
            <>
              <TextField
                {...field}
                id="plumbing-licencee-name"
                value={field.value || ''}
                label={getTextContent('licenseNameInput.label')}
                data-track-value="mask"
                helperText={fieldState.error ? getTextContent('licenseNameInput.invalid') : ''}
                error={!!fieldState.error}
                displayIcon
                required
                fullWidth
                inputProps={{ maxLength: nameFieldLength }}
              />
              <FormSpacer variant="input" />
            </>
          )}
          name="name"
          rules={{
            required: true,
            minLength: 3,
          }}
        />
        <Controller
          control={control}
          render={({ field: { ref: _, ...field }, fieldState }) => (
            <>
              <TextField
                {...field}
                id="plumbing-licencee-name"
                value={field.value || ''}
                label={getTextContent('licenceNumberInput.label')}
                data-track-value="mask"
                helperText={fieldState.error ? getTextContent('licenceNumberInput.invalid') : ''}
                error={!!fieldState.error}
                displayIcon
                required
                fullWidth
                inputProps={{ maxLength: 15 }}
              />
              <FormSpacer variant="input" />
            </>
          )}
          name="number"
          rules={{
            pattern: /^[\da-zA-Z]+$/,
            minLength: 3,
            maxLength: 15,
            required: true,
          }}
        />
      </form>
    </Modal>
  );
};
